import { FC } from "react";
import Box from "@mui/material/Box";
import { ProductCategorySingleSelect } from "@/components/filters/ProductCategory/ProductCategorySingleSelect";
import { MinMaxEditor, hasMinMaxErrors, getHumanMinMax } from "./MinMaxEditor";
import type {
  ScoringType,
  CategoryPenetrationBand,
  MinMaxBand,
} from "@/lib/scoring/types";

// ── Exports for criterion registry ────────────────────────────────────────

export const initialBand: CategoryPenetrationBand = { score: 0 };

export function hasErrors(bands: unknown[]): boolean {
  const typed = bands as CategoryPenetrationBand[];
  if (typed.length === 0) return true;
  if (!typed[0]?.categoryId) return true;
  return hasMinMaxErrors(typed as MinMaxBand[]);
}

export function getBandSummary(band: unknown): string {
  const b = band as CategoryPenetrationBand;
  return getHumanMinMax(b.min, b.max);
}

// ── Component ─────────────────────────────────────────────────────────────

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const CategoryPenetrationEditor: FC<Props> = ({
  criterion,
  onChange,
}) => {
  const bands = criterion.productCategoryPenetration ?? [];
  const categoryId = bands[0]?.categoryId ?? null;

  const handleCategoryChange = (newCategoryId: string | null) => {
    onChange({
      ...criterion,
      productCategoryPenetration:
        bands.length > 0
          ? bands.map((b) => ({ ...b, categoryId: newCategoryId ?? undefined }))
          : [{ ...initialBand, categoryId: newCategoryId ?? undefined }],
    });
  };

  const handleBandsChange = (newBands: MinMaxBand[]) => {
    onChange({
      ...criterion,
      productCategoryPenetration: newBands.map((b) => ({
        ...b,
        categoryId: categoryId ?? undefined,
      })),
    });
  };

  return (
    <>
      <Box sx={{ px: 2, mb: 2 }}>
        <ProductCategorySingleSelect
          selectedCategoryId={categoryId}
          onCategorySelect={handleCategoryChange}
          inputTestId="scoring-category-select-input"
        />
      </Box>

      <MinMaxEditor
        bands={bands}
        onChange={handleBandsChange}
        minLabel="Min Installs"
        maxLabel="Max Installs"
        minInputTestId="scoring-category-min-input"
        maxInputTestId="scoring-category-max-input"
      />
    </>
  );
};
